import { Button, Callout, FormGroup, InputGroup, Intent, Checkbox, Icon, Popover, PopoverPosition, Label } from '@blueprintjs/core'
import { BotConfig, BotTemplate, MessagingConfig, chatTheme } from 'enablex/sdk'
import { ToolTip, lang, toast } from 'enablex/shared'
import _ from 'lodash'
import ms from 'ms'
import React, { Component } from 'react'
import { generatePath, RouteComponentProps } from 'react-router'
import ColorPicker from 'react-pick-color'
import api, { toastError } from '~/app/api'
import style from './style.scss'
import DropzoneComponent from '../bots/DropZoneComponent'
import BrandingIcon from '~/icons/BrandingIcon'
import LoadingSection from '~/app/common/LoadingSection'
import PageContainer from '~/app/common/PageContainer'
import { getActiveWorkspace } from '~/auth/basicAuth'
import { WorkspaceTemplate } from './WorkspaceTemplate'
import { ConfigureForm } from './ConfigureForm'
import { Card } from './Card'
import { fetchBranding } from '../collaborators/reducer'
import { AppState } from '~/app/rootReducer'
import { ConnectedProps, connect } from 'react-redux'
import { PreviewContainer } from './PreviewContainer'
import CopySubdomain from '~/icons/CopySubdomain'
import TrashIcon from '~/icons/TrashIcon'
import RefreshIcon from '~/icons/RefreshIcon'
import SubdomainIcon from '~/icons/SubdomainIcon'
import ThemeIcon from '~/icons/ThemeIcon'

type Props = ConnectedProps<typeof connector> &
RouteComponentProps

interface State {
  selectedFile: any
  logoURL: string
  isLogoAvailable: boolean
  selectedFaviconFile: any
  faviconURL: string
  isFaviconAvailable: boolean
  pageTitle: string
  parentDomain: string
  domain: string
  activeDomain: string
  isChecking: boolean
  isDomainAvailable: boolean
  availableDomain: string
  domainStatus: string
  themeColor: string
  buttonTextColor: string
  backgroundColor: string
  isMainWindowActive: boolean
  activeWindow: string
  workspaceId: string
}

const root = document.documentElement

class Branding extends Component<Props, State> {
  private _form: HTMLFormElement | null = null

  state: State = {
    selectedFile: null,
    logoURL: '',
    isLogoAvailable: false,
    selectedFaviconFile: null,
    faviconURL: '',
    isFaviconAvailable: false,
    pageTitle: '',
    isChecking: false,
    parentDomain: '',
    domain: '',
    activeDomain: '',
    isDomainAvailable: false,
    availableDomain: '',
    domainStatus: '',
    themeColor: '#1d6c90',
    buttonTextColor: '#fff',
    backgroundColor: '',
    isMainWindowActive: true,
    activeWindow: '',
    workspaceId: getActiveWorkspace() || ''
  }

  componentDidMount() {
    const workspaceId = getActiveWorkspace()
    if(workspaceId) {
      this.props.fetchBranding(workspaceId)
      this.setState({
        workspaceId
      })
    }
    const branding: any = this.props.branding || {
      themeColor: '#1d6c90',
      subDomain: '',
      logoURL: '',
      faviconURL: '',
      pageTitle: 'EnableX Dialogs'
    }
    this.updateUI(branding)
  }

  componentDidUpdate(prevProps: Props) {
    if(prevProps.branding !== this.props.branding) {
      this.updateUI(this.props.branding)
    }
  }

  deleteFileName() {
    this.setState({ logoURL: '', isLogoAvailable: false })
  }

  deleteFaviconName() {
    this.setState({ faviconURL: '', isFaviconAvailable: false })
  }

  updateUI(branding) {
    let theme = this.state.themeColor
    if(branding) {
      const {themeColor, subDomain, logoURL, workspaceId, parentDomain, faviconURL, pageTitle}: any = branding
      this.setState({
        parentDomain,
        activeDomain: subDomain,
        logoURL,
        faviconURL,
        isFaviconAvailable: faviconURL ? true : false,
        pageTitle,
        isLogoAvailable: logoURL ? true : false,
        themeColor: themeColor || this.state.themeColor,
        workspaceId: this.props.currentWorkspace || workspaceId || '',
        isMainWindowActive: true
      })
      theme = themeColor || this.state.themeColor
    }
    root.style.setProperty('--template-theme-color', theme)
  }

  convertFileToDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = function (event) {
        resolve(event?.target?.result)
      }

      reader.onerror = function (error) {
        reject(error)
      }

      reader.readAsDataURL(file)
    })
  }

  setFileName(file) {
    // if (!LogoURL) {
    this.convertFileToDataURL(file).then((dataURL: any): any => {
      this.setState({ selectedFile: [file], logoURL: dataURL, isLogoAvailable: false })
      // You can now use the data URL as needed
    }).catch(error => {
      console.error('Error converting file to data URL:', error)
    })
    // }
  }

  setFaviconName(file) {
    // if (!LogoURL) {
    this.convertFileToDataURL(file).then((dataURL: any): any => {
      this.setState({ selectedFaviconFile: [file], faviconURL: dataURL, isFaviconAvailable: false })
      // You can now use the data URL as needed
    }).catch(error => {
      console.error('Error converting file to data URL:', error)
    })
    // }
  }

  isDomainValid(subdomain) {
    const domainPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]$/
    if (!domainPattern.test(subdomain)) {
      this.setState({domainStatus: 'The subdomain must be alphanumeric, with a length between 2 and 15 characters. It should start with an alphabet letter and can contain numbers and hyphens (but cannot start or end with a hyphen).'})
    } else {
      this.setState({domainStatus: ''})
    }
  }

  handleInputChanged(event) {
    this.isDomainValid(event.target.value)
    this.setState({ domain: event.target.value, availableDomain: '', isDomainAvailable: false })
  }

  handlePageTitleChanged(event) {
    this.setState({ pageTitle: event.target.value})
  }

  async checkDomainAvailability() {
    this.setState({isChecking: true})
    try {
      const response = await api.getSecured().get(`/admin/user/workspaces/${this.state.workspaceId}/brandingDomainAvailable/${this.state.domain}`)
      const data = response?.data || undefined
      if(data && data.success) {
        this.setState({availableDomain: data.domain, isDomainAvailable: true, domainStatus: 'Subdomain available', isChecking: false})
      } else if(data && !data.success) {
        this.setState({availableDomain: '', isDomainAvailable: false, domainStatus: 'Subdomain already exist', isChecking: false})
      } else {
        toastError('Failed to check domain availability.')
      }
    } catch(err) {
      this.setState({isChecking: false})
      console.error(err)
    }
  }

  deleteSubdomain() {
    this.setState({isDomainAvailable: false, domain: '', availableDomain: '', activeDomain: ''})
  }

  handleIconColorChange(color) {
    this.setState({ themeColor: color.hex })
    root.style.setProperty('--template-theme-color', color.hex)
  }

  toggleWindowVisibility(key) {
    this.setState({ activeWindow: key, isMainWindowActive: false })
  }

  gotoMainWindow() {
    this.setState({ isMainWindowActive: true, activeWindow: '' })
  }

  async applyBranding() {
    try {

      //upload logo if exist
      let logoPath = this.state.logoURL
      if (this.state.selectedFile) {
        const formData = new FormData()
        formData.append('file', this.state.selectedFile[0])
        const { data } = await api.getSecured().post(`/admin/user/workspaces/${this.state.workspaceId}/uploadBrandLogo`, formData)
        const outputPath = data.file.fileName
        // console.log('logo path: ', data.file.fileUrl.replace('/data', '/api/v1'))
        logoPath = outputPath
      }

      //upload favicon if exist
      let faviconPath = this.state.faviconURL
      if (this.state.selectedFaviconFile) {
        const formData = new FormData()
        formData.append('file', this.state.selectedFaviconFile[0])
        const { data } = await api.getSecured().post(`/admin/user/workspaces/${this.state.workspaceId}/uploadBrandLogo`, formData)
        const outputPath = data.file.fileName
        // console.log('favicon path: ', data.file.fileUrl.replace('/data', '/api/v1'))
        faviconPath = outputPath
      }
      const payload = {
        subDomain: this.state.isDomainAvailable ? this.state.availableDomain : this.state.activeDomain,
        logoURL: logoPath,
        faviconURL: faviconPath,
        pageTitle: this.state.pageTitle,
        themeColor: this.state.themeColor || '',
      }
      if(logoPath || faviconPath) {
       await api.getSecured().post(`/admin/user/workspaces/${this.state.workspaceId}/clearOldBrandFiles`, {
          'files': [logoPath, faviconPath]
        })
      }
      await api.getSecured().post(`/admin/user/workspaces/${this.state.workspaceId}/applyBranding`, payload)
      this.setState({isMainWindowActive: true})
      toast.success('Branding configured successfully.')
      localStorage.setItem('enx/branding', JSON.stringify(payload))
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    } catch (err) {
      console.log(err.message)
      toast.failure('Something went wrong. Could not apply changes')
    }
  }

  resetColor() {
    this.setState({ themeColor: '#1d6c90' })
    root.style.setProperty('--template-theme-color', '#1d6c90')
  }

  copySubDomain(domainID) {
    const subDomain = document.getElementById(`${domainID}`)
    const codeText = subDomain?.textContent
    if(codeText) {
      navigator.clipboard.writeText(codeText)
      .then(() => {
          console.log('Subdomain copied to clipboard')
          toast.info('Subdomain copied to clipboard')
      })
      .catch((error) => {
          console.error('Failed to copy subdomain to clipboard:', error)
      })
    }
  }

  render() {
    if (this.props.loading) {
      return <LoadingSection />
    }

    const SectionHeading = ({heading, description}: any) => {
      return <>
        { heading && <p className={style.subHeading}>{heading}</p> }
        { description && <p className={style.subHeadingDescription}>{description}</p> }
      </>
    }

    const isWorkspaceLogo = this.props.branding?.logoURL && this.props.branding?.logoURL.includes('workspace') ? true : false
    const selectedImage = this.state.selectedFile ? this.state.selectedFile : this.state.workspaceId && this.props.branding?.logoURL ? `/api/v1/bots/___/media/${this.state.workspaceId}/${this.props.branding?.logoURL}` : !isWorkspaceLogo ? this.state.logoURL : ''
    const isWorkspaceFavicon = this.props.branding?.faviconURL && this.props.branding?.faviconURL.includes('workspace') ? true : false
    const selectedFaviconImage = this.state.selectedFaviconFile ? this.state.selectedFaviconFile : this.state.workspaceId && this.props.branding?.faviconURL ? `/api/v1/bots/___/media/${this.state.workspaceId}/${this.props.branding?.faviconURL}` : !isWorkspaceFavicon ? this.state.faviconURL : ''
    const currentActiveWorkspace = this.props.workspaces?.find(x => x.workspace === this.state.workspaceId)
    const workspacName = currentActiveWorkspace?.workspaceName
    return (
      <>
      <div className={style.mainHeader}>
        <div>
          <p style={{fontSize: '20px', marginBottom: '4px', color: 'var(--theme-color)'}}>Branding</p>
          <p style={{ fontSize: '14px', fontWeight: 600, marginBottom: 0, color: '#333' }}><span style={{color: 'var(--theme-color)'}}>{workspacName}</span>&nbsp;/&nbsp;Branding</p>
        </div>
        <Button
          id="btn-apply-theme"
          text={lang.tr('admin.workspace.bots.applyChanges')}
          intent="primary"
          className={style.applyChanges}
          onClick={this.applyBranding.bind(this)}
        />
      </div>
      <div className={style.pageContentContainer}>
        <div className={style.contentWrapper}>
            <div className={style.contentHeader}>
              <div>
                <p style={{fontSize: '34px', fontFamily: 'Barlow Medium', color: 'var(--theme-color)'}}>Workspace Branding</p>
              </div>
            </div>
            <div className={style.configWrapper}>
              <div className={style.formWrapper}>
                {/* theme config */}
                <section className={style.sectionWrapper}>
                  <SectionHeading heading={'Theme'} description={'Configure your workspace with custom theme and logo.'} />
                  {/* Brand Name */}
                  <Label style={{fontSize: '18px', fontFamily: 'Barlow SemiBold'}}>Enter brand name</Label>
                  <FormGroup
                    className={style.pageTitleWrapper}
                    labelFor="input-page-title"
                    helperText={'This is the brand identity of the dashboard.'}
                  >
                    <InputGroup
                      id="input-page-title"
                      tabIndex={1}
                      placeholder={'e.g., EnableX Dialogs'}
                      minLength={3}
                      maxLength={30}
                      value={this.state.pageTitle}
                      onChange={this.handlePageTitleChanged.bind(this)}
                    />
                  </FormGroup>
                  {/* Brand Logo */}
                  <div>
                    <div
                      // className={style.botAvatar}
                      style={{ overflow: 'hidden', display: 'flex', paddingBottom: '20px' }}
                    >
                      <div style={{ width: '100%', minWidth: '275px' }}>
                        <label style={{fontSize: '18px', fontFamily: 'Barlow SemiBold'}}>Select brand logo</label>
                        <p style={{ marginTop: '5px', color: '#666', fontSize: '16px', paddingBottom: '10px', fontFamily: 'Barlow', fontWeight: 100 }}>
                          This image will be the logo of the current workspace. Drag and drop an associated image here.
                        </p>
                        <DropzoneComponent selectedImage={selectedImage} botAvatar={this.setFileName.bind(this)} handleDeleteFileName={this.deleteFileName.bind(this)} customIcon={<BrandingIcon width="44" height="44" color="#333" style={{ border: '1px solid rgb(200 198 198)', padding: '15px', borderRadius: '50%' }} />} />
                      </div>
                    </div>
                  </div>
                  {/* Favicon */}
                  <div>
                    <div
                      // className={style.botAvatar}
                      style={{ overflow: 'hidden', display: 'flex', paddingBottom: '20px' }}
                    >
                      <div style={{ width: '100%', minWidth: '275px' }}>
                        <label style={{ fontSize: '18px', fontFamily: 'Barlow SemiBold' }}>Select a favicon</label>
                        <p style={{ marginTop: '5px', color: '#666', fontSize: '16px', paddingBottom: '10px', fontFamily: 'Barlow', fontWeight: 100 }}>
                          This icon will be the logo for the page as you can see on the right side. Drag and drop an associated image here.
                        </p>
                        <DropzoneComponent selectedImage={selectedFaviconImage} botAvatar={this.setFaviconName.bind(this)} handleDeleteFileName={this.deleteFaviconName.bind(this)} customIcon={<BrandingIcon width="44" height="44" color="#333" style={{ border: '1px solid rgb(200 198 198)', padding: '15px', borderRadius: '50%' }} />} />
                      </div>
                    </div>
                  </div>
                  {/* Theme Color */}
                  <div
                    className={style.themeColor}
                    style={{ overflow: 'hidden', display: 'flex', paddingBottom: '20px' }}
                  >
                    <div style={{ width: '100%', minWidth: '275px' }}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <label style={{fontSize: '18px', fontFamily: 'Barlow SemiBold'}}>Select theme color</label>
                        <ToolTip content="Reset theme">
                          <Button
                            className={style.resetColorButton}
                            icon={<RefreshIcon />}
                            onClick={this.resetColor.bind(this)}
                            small
                          ></Button>
                        </ToolTip>
                      </div>
                      <p style={{ marginTop: '5px', color: '#666', fontSize: '16px', paddingBottom: '10px', fontFamily: 'Barlow', fontWeight: 100 }}>
                        Configure theme color and see on the right how it looks in the preview.
                      </p>
                      <ColorPicker color={this.state.themeColor} onChange={this.handleIconColorChange.bind(this)} className={style.colorPicker} />
                    </div>
                  </div>
                </section>
                {/* sub domain config */}
                <section className={style.sectionWrapper}>
                  <SectionHeading heading={'Subdomain'} description={'Configure your workspace with custom subdomain, brand identity and favicon.'} />
                  <Label style={{fontSize: '18px', fontFamily: 'Barlow SemiBold'}}>Enter subdomain</Label>
                  <div className={style.checkAvailabilityWrapper}>
                    <div style={{display: 'flex'}}>
                      <FormGroup style={{ width: '100%' }} helperText={<span style={{color: `${this.state.isDomainAvailable ? '#7EC01F' : '#EF1706'}`}}>{this.state.domainStatus}</span>}>
                        <InputGroup
                          leftElement={<span className={`${style.inputContent} ${style.leftContent}`}>https://</span>}
                          id="domain"
                          placeholder="e.g., enablex"
                          name="domain"
                          maxLength={15}
                          minLength={2}
                          value={this.state.domain}
                          onChange={this.handleInputChanged.bind(this)}
                        />
                      </FormGroup>
                      <span style={{position: 'relative'}} className={`${style.inputContent} ${style.rightContent}`}>.{this.state.parentDomain ? this.state.parentDomain : window.location.host}</span>
                    </div>
                    <FormGroup>
                      <Button
                        text={'Check Availability'}
                        intent="primary"
                        disabled={(this.state.domain ? false : true) || this.state.isChecking || (this.state.domainStatus ? true : false)}
                        loading={this.state.isChecking}
                        onClick={this.checkDomainAvailability.bind(this)}
                        className={style.button}
                      />
                    </FormGroup>
                  </div>
                  {
                    this.state.availableDomain ? <div className={style.newDomainWrapper}>
                      <span><span>New Domain: </span><span id="new-subdomain" style={{color: '#2196F3', fontFamily: 'monospace'}}>https://{this.state.availableDomain}</span></span>
                      <ToolTip content="Copy">
                        <Button
                          className={style.copyButton}
                          icon={<CopySubdomain />}
                          onClick={this.copySubDomain.bind(this, 'new-subdomain')}
                          small
                        ></Button>
                      </ToolTip>
                    </div> : <></>
                  }
                  {
                    this.state.activeDomain ? <div className={style.activeDomainWrapper}>
                      <span><span>Active Domain: </span><span id="active-subdomain" style={{color: '#7EC01F', fontFamily: 'monospace'}}>https://{this.state.activeDomain}</span></span>
                      <ToolTip content="Copy">
                        <Button
                          className={style.copyButton}
                          icon={<CopySubdomain />}
                          onClick={this.copySubDomain.bind(this, 'active-subdomain')}
                          small
                        ></Button>
                      </ToolTip>
                      <ToolTip content="Delete">
                        <Button
                          className={style.trashButton}
                          icon={<TrashIcon />}
                          onClick={this.deleteSubdomain.bind(this)}
                          small
                        ></Button>
                      </ToolTip>
                    </div> : <></>
                  }
                </section>
              </div>
              <div className={style.previewWrapper}>
                <PreviewContainer faviconURL={this.state.faviconURL} pageTitle={this.state.pageTitle} isFaviconAvailable={this.state.isFaviconAvailable} workspaceId={this.state.workspaceId} />
                <WorkspaceTemplate config={this.state} />
              </div>
            </div>
        </div>
      </div>
      </>
    )
  }
}

const mapStateToProps = (state: AppState) => ({
  loading: state.collaborators.brandingLoading,
  branding: state.collaborators.branding,
  currentWorkspace: state.user.currentWorkspace,
  workspaces: state.user.workspaces
})
const connector = connect(mapStateToProps, { fetchBranding })

export default connector(Branding)
